import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, IconButton, Loading, Modal, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useVehicleTypeMigrationCheck } from "@/hooks/useVehicleTypeMigrationCheck";
import { useArchiveVehicleMutation, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useAppDispatch } from "@/redux/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, getAssetUrl } from "@/utils";
import { VehicleTypesSetupModal } from "../VehicleTypesSetupModal";

interface DraftVehicleCardProps {
  vehicle: Vehicle;
}

const TOTAL_STEPS = 4;

export const DraftVehicleCard = ({ vehicle }: DraftVehicleCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentStep = vehicle.setupStep ?? 1;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const { needsMigration: needsTypeMigration, valid: typesValid, total: typesTotal } = useVehicleTypeMigrationCheck();
  const [archiveVehicle, { isLoading }] = useArchiveVehicleMutation();

  const handleDelete = () => {
    archiveVehicle(vehicle.uuid)
      .unwrap()
      .then(() => {
        setShowDeleteModal(false);
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
        addToast("success", "Vehicle draft has been deleted");
      })
      .catch((e) => {
        getErrorMessages(e).forEach((m) => addToast("danger", m));
      });
  };

  return (
    <>
      <div className="flex items-center justify-between rounded-lg border border-neutral-mid-gray bg-white p-2.5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-mid-gray">
            <img
              src={getAssetUrl(vehicle.model.avatar, { height: 56, width: 56, background: "transparent", fit: "contain" })}
              className="h-6 w-6"
              alt={`${vehicle.model.make} ${vehicle.model.model}`}
            />
          </div>
          <Typography variant="action">
            {vehicle.model.make} {vehicle.model.model} ({vehicle.registrationNumber})
          </Typography>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-danger">
            <Icon name="InfoCircle" size="md" />
            <Typography variant="paragraph" className="text-danger">
              Incomplete — Step {currentStep} of {TOTAL_STEPS}
            </Typography>
          </div>
          <Button variant="primary" size="sm" onClick={() => (needsTypeMigration ? setShowSetupModal(true) : navigate(`add/${vehicle.uuid}`))}>
            Continue
          </Button>
          <IconButton
            iconName="close"
            isCustomIcon
            variant="custom"
            iconSize="lg"
            className="text-neutral-dark-gray hover:text-danger"
            onClick={() => setShowDeleteModal(true)}
          />
        </div>
      </div>
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} className="w-[85vw] !p-6 md:w-[400px] lg:w-[400px]">
        <div className="relative flex flex-col">
          {isLoading && <Loading />}
          <Typography variant="title">Delete vehicle setup?</Typography>
          <Typography variant="paragraph" className="mt-3 text-neutral-dark-gray">
            This vehicle setup isn&apos;t complete. Deleting it will remove all entered information.
          </Typography>
          <div className="mt-6 flex justify-end gap-4">
            <Button variant="secondary" size="md" onClick={() => setShowDeleteModal(false)}>
              Back
            </Button>
            <Button
              variant="primary"
              size="md"
              className="!bg-danger !text-white hover:!bg-danger-dark"
              onClick={handleDelete}
              disabled={isLoading}
            >
              Delete Vehicle
            </Button>
          </div>
        </div>
      </Modal>
      <VehicleTypesSetupModal open={showSetupModal} onClose={() => setShowSetupModal(false)} valid={typesValid} total={typesTotal} />
    </>
  );
};
